import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useState } from "react";
import { SectionHeading } from "@/components/Atoms";
import { TinyCard, TINY_GRID } from "@/components/Card3D";
import { PaginationControls } from "@/components/PaginationControls";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Search, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const RARITIES = ["all", "common", "uncommon", "rare", "epic", "legendary", "goddess"];

export default function ExplorePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [hideOwned, setHideOwned] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["explore", filter, page, pageSize, q, hideOwned],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(page * pageSize),
      });
      if (filter !== "all") params.set("rarity", filter);
      if (q) params.set("q", q);
      if (hideOwned && user) params.set("hide_owned", "true");
      return (await api.get(`/cards/explore?${params}`)).data;
    },
    keepPreviousData: true,
  });

  const items = data?.items || [];
  const total = data?.total || 0;
  const totalMinted = data?.total_minted || 0;
  const totalUnminted = data?.total_unminted || 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleSearch = (e) => {
    e?.preventDefault();
    setQ(searchInput.trim());
    setPage(0);
  };

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-5 sm:py-10">
      <SectionHeading
        eyebrow="Discover"
        title="EXPLORE"
        description="These are the cards that have already been minted — duplicate editions are grouped, so each design shows once with how many editions exist and how many are still unminted (waiting to be discovered). Hover to preview the art; click to open it."
      />

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-4">
        <div className="-mx-1 overflow-x-auto lg:overflow-visible">
          <Tabs value={filter} onValueChange={(v) => { setFilter(v); setPage(0); }}>
            <TabsList className="bg-[hsl(220_18%_10%)] border border-white/10 flex flex-nowrap">
              {RARITIES.map((r) => (
                <TabsTrigger key={r} value={r} data-testid={`explore-filter-${r}`} className="data-[state=active]:bg-white/10 data-[state=active]:text-white text-white/60 uppercase tracking-[0.2em] text-[10px] whitespace-nowrap">
                  {r}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>

        <form onSubmit={handleSearch} className="flex gap-2 lg:ml-auto w-full lg:w-auto lg:min-w-[320px]">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
            <Input
              data-testid="explore-search-input"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search by serial or title"
              className="pl-9 bg-[hsl(220_18%_12%)] border-white/10"
            />
          </div>
          <Button data-testid="explore-search-submit" type="submit" variant="outline" className="border-white/15 text-white">Search</Button>
        </form>
      </div>

      {user && (
        <label
          htmlFor="hide-owned-toggle"
          data-testid="explore-hide-owned"
          className="hud-pill rounded-full px-3.5 py-1.5 inline-flex items-center gap-2.5 cursor-pointer select-none mb-4 hover:border-cyan-400/30 transition-colors"
        >
          <EyeOff className={`h-4 w-4 transition-colors ${hideOwned ? "text-cyan-300" : "text-white/45"}`} />
          <span className={`text-sm font-medium transition-colors ${hideOwned ? "text-white" : "text-white/60"}`}>Hide cards I own</span>
          <Switch
            id="hide-owned-toggle"
            data-testid="explore-hide-owned-switch"
            checked={hideOwned}
            onCheckedChange={(v) => { setHideOwned(v); setPage(0); }}
            className="data-[state=checked]:bg-cyan-400"
          />
        </label>
      )}

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-xs text-white/40 mb-4" data-testid="explore-supply-summary">
        <span>{total.toLocaleString()} design{total === 1 ? "" : "s"} minted</span>
        <span className="text-white/20">·</span>
        <span className="text-emerald-300/80" data-testid="explore-total-minted">{totalMinted.toLocaleString()} cards minted</span>
        <span className="text-white/20">·</span>
        <span className="text-amber-300/80" data-testid="explore-total-unminted">{totalUnminted.toLocaleString()} still unminted</span>
        <span className="text-white/20">·</span>
        <span>Page {Math.min(page + 1, totalPages)} / {totalPages}</span>
      </div>

      {isLoading ? (
        <div className="text-center text-white/40 py-12">Loading…</div>
      ) : items.length === 0 ? (
        <div className="hud-pill rounded-2xl p-10 text-center text-white/50">No cards found{q ? ` for "${q}"` : ""}.</div>
      ) : (
        <>
          <div className={TINY_GRID}>
            {items.map((c) => (
              <TinyCard
                key={c.id}
                card={c}
                count={c.stack_count || 1}
                idKey={c.template_id || c.id}
                onClick={() => navigate(`/card/${c.id}`)}
                footer={
                  <div className="mt-1 px-0.5" data-testid={`explore-supply-${c.template_id || c.id}`}>
                    <div className="font-mono text-[9px] text-white/60 truncate">{c.title || "Card"}</div>
                    <div className="font-mono text-[8px]">
                      <span className="text-emerald-300/80">{(c.minted_count ?? c.stack_count ?? 0).toLocaleString()} minted</span>
                      {c.edition_count ? (
                        <span className="text-white/40"> · {(c.unminted ?? 0).toLocaleString()} unminted</span>
                      ) : null}
                    </div>
                  </div>
                }
              />
            ))}
          </div>

          <PaginationControls
            page={page}
            total={total}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
            testidPrefix="explore"
          />
        </>
      )}
    </div>
  );
}
